import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const EMPTY_ACTION = {
  type: 'send_notification', target_group_id: '', target_column_id: 'status', target_value: '',
  notification_message: '', delay_minutes: 0,
  email_to: '', email_subject: '', email_body: '',
  send_to_assignee: false, send_to_all_members: false,
  assign_person_user_id: '',
};

const NEXOSYNX_LOGO_SVG = `<svg width="44" height="44" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs><linearGradient id="ngrad" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
    <stop offset="0%" stop-color="#2563EB"/><stop offset="100%" stop-color="#00D4CB"/>
  </linearGradient></defs>
  <path d="M10 44 L10 14 L18 14 L36 36 L36 14 L44 14 L44 44 L36 44 L18 22 L18 44 Z" fill="url(#ngrad)"/>
  <circle cx="36" cy="10" r="5" fill="#00D4CB"/>
  <circle cx="28" cy="44" r="2.5" fill="#00D4CB" opacity="0.7"/>
</svg>`;

function renderEmailVars(text) {
  const sample = {
    '{{item_name}}': 'Proposta Comercial',
    '{{new_value}}': 'Em andamento',
    '{{board_name}}': 'Vendas 2026',
    '{{item_status}}': 'Em andamento',
    '{{person_name}}': 'Maria Silva',
    '{{person_email}}': 'maria@exemplo.com',
  };
  let out = text || '';
  Object.entries(sample).forEach(([k, v]) => { out = out.split(k).join(v); });
  return out;
}

function buildEmailPreviewHtml(content) {
  const body = renderEmailVars(content).replace(/\n/g, '<br/>');
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;background:#0B1220;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#0B1220;padding:28px 16px;">
    <tr><td align="center">
      <table role="presentation" width="640" cellpadding="0" cellspacing="0" style="max-width:640px;background:#0f172a;border-radius:16px;overflow:hidden;font-family:'Segoe UI',Arial,Helvetica,sans-serif;border:1px solid rgba(37,99,235,0.25);">
        <tr>
          <td style="background:linear-gradient(135deg,#2563EB,#00D4CB);padding:22px 32px;">
            <table role="presentation" cellpadding="0" cellspacing="0">
              <tr>
                <td valign="middle" style="padding-right:12px;">${NEXOSYNX_LOGO_SVG}</td>
                <td valign="middle">
                  <span style="color:#ffffff;font-size:20px;font-weight:700;letter-spacing:-0.02em;">NexoSynX CRM</span><br/>
                  <span style="color:rgba(255,255,255,0.85);font-size:12px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;">Gestão de Projetos</span>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding:28px 32px;color:#E5E7EB;font-size:15px;line-height:1.7;">${body || '<p style="color:#64748b;font-style:italic;">Digite o conteúdo do e-mail para ver a prévia aqui...</p>'}</td>
        </tr>
        <tr>
          <td style="background:#0f172a;padding:20px 32px;border-top:1px solid rgba(37,99,235,0.25);">
            <p style="margin:0;color:#94a3b8;font-size:12px;line-height:1.6;">Este e-mail foi enviado automaticamente pelo <strong style="color:#00D4CB;">NexoSynX CRM</strong>.<br/>Responda diretamente a este e-mail caso tenha dúvidas.</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function AutomationForm({ board, api, onSave, onCancel, initialData }) {
  const isEdit = Boolean(initialData?.id);
  const firstAction = initialData?.actions?.[0] || initialData?.action || null;
  const [form, setForm] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    trigger: initialData?.trigger || { type: 'status_change', column_id: 'status', from_value: '', to_value: '' },
    action: firstAction ? { ...EMPTY_ACTION, ...firstAction } : { ...EMPTY_ACTION },
    active: initialData?.active ?? true,
    recurrence: initialData?.recurrence || '',
    recurrence_action: initialData?.recurrence_action || '',
    recurrence_config: initialData?.recurrence_config || {},
    next_run: '',
  });
  const [saving, setSaving] = useState(false);
  const [boardMembers, setBoardMembers] = useState([]);
  const [boardGroups, setBoardGroups] = useState([]);
  const [boardColumns, setBoardColumns] = useState([]);
  const [boardItems, setBoardItems] = useState([]);
  const [boardStatuses, setBoardStatuses] = useState([]);


 useEffect(() => {
  if (!board?.id) return;
  Promise.allSettled([
    api.get(`/projects/boards/${board.id}/groups`),
    api.get(`/projects/boards/${board.id}/columns`),
    api.get(`/projects/boards/${board.id}/items`),
    api.get(`/projects/boards/${board.id}/members`),
  ]).then(([g, c, i, m]) => {
    if (g.status === 'fulfilled') setBoardGroups(g.value.data || []);
    if (c.status === 'fulfilled') setBoardColumns(c.value.data || []);
    if (m.status === 'fulfilled') setBoardMembers(m.value.data || []);
    if (i.status === 'fulfilled') {
      const allItems = i.value.data?.items || i.value.data || [];
      setBoardItems(allItems);
      // Extrair status únicos dos itens
      const statusSet = new Set();
      allItems.forEach(item => {
        const s = item.column_values?.status || item.status;
        if (s) statusSet.add(s);
        // sub-tarefas
        (item.subitems || item.sub_items || []).forEach(sub => {
          const ss = sub.column_values?.status || sub.status;
          if (ss) statusSet.add(ss);
        });
      });
      setBoardStatuses([...statusSet]);
    }
  });
}, [board?.id]);


  const TRIGGER_TYPES = [
    { value: 'status_change', label: 'Status muda' },
    { value: 'priority_change', label: 'Prioridade muda' },
    { value: 'person_change', label: 'Responsável muda' },
    { value: 'item_completed', label: 'Item concluído (status = feito/done)' },
    { value: 'due_date_passed', label: 'Prazo passou' },
  ];
  // Cada tipo de gatilho observa uma coluna diferente do item. Sem esse mapa, o
  // trigger.column_id ficava sempre travado em "status" (valor inicial do form),
  // então gatilhos como "Prioridade muda" ou "Responsável muda" comparavam a
  // coluna errada no backend e nunca disparavam.
  const TRIGGER_COLUMN_MAP = {
    status_change: 'status',
    priority_change: 'priority',
    person_change: 'assigned_members',
    item_completed: 'status',
    due_date_passed: 'due_date',
  };
  const PRIORITY_OPTIONS = ['baixa', 'media', 'alta'];
  const needsFromTo = ['status_change', 'priority_change'].includes(form.trigger.type)
    || form.trigger.type.startsWith('column_');
  const triggerValueOptions = form.trigger.type === 'priority_change' ? PRIORITY_OPTIONS : boardStatuses;
  const ACTION_TYPES = [
    { value: 'send_notification', label: 'Enviar notificação interna' },
    { value: 'send_email', label: '📧 Enviar e-mail' },
    { value: 'assign_person', label: '👤 Atribuir pessoa' },
    { value: 'set_completion_100', label: '✅ Marcar conclusão 100%' },
    { value: 'move_to_group', label: 'Mover para grupo' },
    { value: 'fire_webhook', label: 'Disparar webhook' },
  ];

  const handleSave = async () => {
    if (!form.name) { toast.error('Nome obrigatório'); return; }
    if (needsFromTo && !form.trigger.to_value) {
      toast.error('Selecione o valor de destino do gatilho ("Para")'); return;
    }
    if (form.action.type === 'move_to_group' && !form.action.target_group_id) {
      toast.error('Selecione o grupo destino da ação'); return;
    }
    if (form.action.type === 'assign_person' && !form.action.assign_person_user_id) {
      toast.error('Selecione o membro a atribuir'); return;
    }
    if (form.action.type === 'send_email' && !form.action.send_to_assignee && !form.action.send_to_all_members && !(form.action.email_to || '').trim()) {
      toast.error('Selecione ao menos um destinatário para o e-mail'); return;
    }
    setSaving(true);
    try {
      // Auto-corrige o column_id a partir do tipo selecionado — cobre também
      // automações antigas, salvas antes dessa correção, com column_id errado.
      const correctedColumnId = form.trigger.type.startsWith('column_')
        ? form.trigger.type.slice('column_'.length)
        : (TRIGGER_COLUMN_MAP[form.trigger.type] || form.trigger.column_id);
      const payload = {
        name: form.name, description: form.description,
        trigger: { ...form.trigger, column_id: correctedColumnId },
        actions: [{ ...form.action, delay_minutes: parseInt(form.action.delay_minutes) || 0 }],
        active: form.active,
        recurrence: form.recurrence || null,
        recurrence_action: form.recurrence_action || null,
        recurrence_config: form.recurrence_config || {},
      };
      if (isEdit) {
        await api.put(`/projects/automations/${initialData.id}`, payload);
        toast.success('Automação atualizada!');
      } else {
        await api.post(`/projects/boards/${board.id}/automations`, payload);
        toast.success('Automação criada!');
      }
      onSave();
    } catch (e) {
      toast.error(isEdit ? 'Erro ao atualizar automação' : 'Erro ao criar automação');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label>Nome *</Label>
          <Input className="mt-1" placeholder="Ex: Notificar quando concluído" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div className="col-span-2">
          <Label>Descrição</Label>
          <Input className="mt-1" placeholder="Opcional" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
      </div>

      <div className="border rounded-lg p-3 space-y-3 bg-blue-50/40">
        <p className="text-sm font-semibold text-gray-700">⚡ Gatilho — Quando...</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select
              value={form.trigger.type}
              onValueChange={v => setForm(f => ({
                ...f,
                trigger: {
                  ...f.trigger,
                  type: v,
                  column_id: v.startsWith('column_') ? v.slice('column_'.length) : (TRIGGER_COLUMN_MAP[v] || 'status'),
                  // Limpa De/Para ao trocar de tipo — valores de um campo (ex: prioridade)
                  // não fazem sentido para outro (ex: status) e ficariam presos.
                  from_value: '',
                  to_value: '',
                }
              }))}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{TRIGGER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              {boardColumns.map(col => (
                 <SelectItem key={col.id} value={`column_${col.id}`}>{col.title} muda</SelectItem>
                 ))}
              </SelectContent>
            </Select>
          </div>
          {needsFromTo && (<>
            <div>
              <Label className="text-xs">De (vazio = qualquer)</Label>
              <Select value={form.trigger.from_value} onValueChange={v => setForm(f => ({ ...f, trigger: { ...f.trigger, from_value: v } }))}>
                <SelectTrigger><SelectValue placeholder="qualquer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">qualquer</SelectItem>
                  {triggerValueOptions.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
             <Label className="text-xs">Para (novo valor) *</Label>
                <Select value={form.trigger.to_value} onValueChange={v => setForm(f => ({ ...f, trigger: { ...f.trigger, to_value: v } }))}>
                  <SelectTrigger><SelectValue placeholder="selecione o valor" /></SelectTrigger>
                  <SelectContent>
                    {triggerValueOptions.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
            </div>
          </>)}
          {form.trigger.type === 'person_change' && (
            <div className="col-span-2 text-xs text-gray-500 bg-gray-50 rounded p-2">
              Dispara sempre que o responsável do item for alterado (qualquer pessoa).
            </div>
          )}
          {form.trigger.type === 'due_date_passed' && (
            <div className="col-span-2 text-xs text-gray-500 bg-gray-50 rounded p-2">
              Dispara quando o prazo do item vence.
            </div>
          )}
        </div>
      </div>
      <div className="border rounded-lg p-3 space-y-3 bg-green-50/40">
        <p className="text-sm font-semibold text-gray-700">🎯 Ação — Então...</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Tipo de ação</Label>
            <Select value={form.action.type} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, type: v } }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTION_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.action.type === 'send_notification' && (
            <div className="col-span-2">
              <Label className="text-xs">Mensagem</Label>
              <Input className="mt-1" placeholder="Texto da notificação" value={form.action.notification_message} onChange={e => setForm(f => ({ ...f, action: { ...f.action, notification_message: e.target.value } }))} />
            </div>
          )}
          {form.action.type === 'send_email' && (<>
            {/* Recipient options */}
            <div className="col-span-2 space-y-2">
              <Label className="text-xs font-semibold">Destinatário</Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.action.send_to_assignee || false}
                    onChange={e => setForm(f => ({ ...f, action: { ...f.action, send_to_assignee: e.target.checked } }))}
                    className="rounded" />
                  Responsável do item (e-mail do usuário atribuído)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.action.send_to_all_members || false}
                    onChange={e => setForm(f => ({ ...f, action: { ...f.action, send_to_all_members: e.target.checked } }))}
                    className="rounded" />
                  Todos os membros do projeto
                </label>
                <div>
                  <Label className="text-xs text-gray-500">E-mail adicional (opcional)</Label>
                  <Input className="mt-1" placeholder="email@exemplo.com"
                    value={form.action.email_to || ''}
                    onChange={e => setForm(f => ({ ...f, action: { ...f.action, email_to: e.target.value } }))} />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Assunto</Label>
              <Input className="mt-1" placeholder="Atualização: {{item_name}}" value={form.action.email_subject || ''} onChange={e => setForm(f => ({ ...f, action: { ...f.action, email_subject: e.target.value } }))} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Corpo do e-mail</Label>
              <textarea
                className="mt-1 w-full min-h-[200px] p-3 rounded-lg border border-input bg-white text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={'Olá,\n\nO item "{{item_name}}" mudou para "{{new_value}}".\n\nAcompanhe o andamento no NexoSynX CRM!'}
                value={form.action.email_body || ''}
                onChange={e => setForm(f => ({ ...f, action: { ...f.action, email_body: e.target.value } }))}
              />
            </div>
            <div className="col-span-2 text-xs text-gray-400 bg-gray-50 rounded p-2">
              Variáveis: <code>{'{{item_name}}'}</code> · <code>{'{{new_value}}'}</code> · <code>{'{{board_name}}'}</code> · <code>{'{{item_status}}'}</code> · <code>{'{{person_name}}'}</code> · <code>{'{{person_email}}'}</code>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-semibold text-gray-700 mb-2">👁️ Prévia do e-mail</p>
              <div className="rounded-lg overflow-hidden border border-gray-300 bg-white">
                <iframe
                  title="Prévia do e-mail"
                  srcDoc={buildEmailPreviewHtml(form.action.email_body || '')}
                  sandbox=""
                  className="w-full h-72 bg-[#0B1220]"
                />
              </div>
            </div>
          </>)}
          {form.action.type === 'assign_person' && (
            <div className="col-span-2 space-y-2">
              <Label className="text-xs font-semibold">Membro a atribuir</Label>
              <Select value={form.action.assign_person_user_id || ''} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, assign_person_user_id: v } }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar membro…" /></SelectTrigger>
                <SelectContent>
                  {boardMembers.map(m => (
                    <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                      {m.user_name || m.name || m.user_email || 'Membro'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400">Este membro será adicionado ao item como responsável quando o gatilho ocorrer.</p>
            </div>
          )}
          {form.action.type === 'set_completion_100' && (
            <div className="col-span-2 text-xs text-green-700 bg-green-50 rounded p-2">
              ✅ Quando o gatilho ocorrer, a conclusão do item será automaticamente definida como 100%.
            </div>
          )}
          {form.action.type === 'move_to_group' && (
            <div className="col-span-2">
              <Label className="text-xs">Grupo destino</Label>
              <Select value={form.action.target_group_id} onValueChange={v => setForm(f => ({ ...f, action: { ...f.action, target_group_id: v } }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione um grupo" /></SelectTrigger>
                <SelectContent>
                  {boardGroups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name || g.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">⏱ Atraso (minutos) — 0 = imediato</Label>
            <Input type="number" min="0" className="mt-1" value={form.action.delay_minutes} onChange={e => setForm(f => ({ ...f, action: { ...f.action, delay_minutes: e.target.value } }))} />
          </div>
        </div>
      </div>

      <div className="border rounded-lg p-3 space-y-3 bg-purple-50/40">
        <p className="text-sm font-semibold text-gray-700">🔁 Recorrência (opcional)</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Frequência</Label>
            <Select value={form.recurrence || 'none'} onValueChange={v => setForm(f => ({ ...f, recurrence: v === 'none' ? '' : v }))}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Nenhuma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma</SelectItem>
                <SelectItem value="daily">Diária</SelectItem>
                <SelectItem value="weekly">Semanal</SelectItem>
                <SelectItem value="monthly">Mensal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.recurrence && (<>
            <div><Label className="text-xs">Primeira execução</Label><Input type="datetime-local" className="mt-1" value={form.next_run} onChange={e => setForm(f => ({ ...f, next_run: e.target.value }))} /></div>
            <div>
              <Label className="text-xs">Ação recorrente</Label>
              <Select value={form.recurrence_action || ''} onValueChange={v => setForm(f => ({ ...f, recurrence_action: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent><SelectItem value="create_item">Criar item</SelectItem></SelectContent>
              </Select>
            </div>
            {form.recurrence_action === 'create_item' && (
              <div><Label className="text-xs">Nome do item</Label><Input className="mt-1" placeholder="Item recorrente" value={form.recurrence_config.item_name || ''} onChange={e => setForm(f => ({ ...f, recurrence_config: { ...f.recurrence_config, item_name: e.target.value } }))} /></div>
            )}
          </>)}
        </div>
      </div>

      <div className="flex space-x-2 justify-end pt-2 border-t">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Criar Automação'}</Button>
      </div>
    </div>
  );
}

export default function ProjectAutomations({ board, api, onDataChange }) {
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  // Incrementado toda vez que o modal "Nova Automação" é aberto, para forçar
  // o React a remontar o AutomationForm com estado limpo — sem isso, o
  // formulário mantinha os valores (gatilho/ação) da automação anterior e
  // uma automação "contaminava" a próxima criada na sequência.
  const [createFormKey, setCreateFormKey] = useState(0);
  const openCreateModal = () => { setCreateFormKey(k => k + 1); setShowCreateModal(true); };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const r = await api.post(`/projects/boards/${board.id}/send-test-email`);
      toast.success(r.data.message || 'E-mail de teste enviado!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar e-mail de teste');
    } finally {
      setSendingTest(false);
    }
  };

  useEffect(() => {
    if (board) {
      fetchAutomations();
      fetchTemplates();
    }
  }, [board]);

  const fetchAutomations = async () => {
    if (!board) return;
    
    try {
      const response = await api.get(`/projects/boards/${board.id}/automations`);
      setAutomations(response.data || []);
    } catch (error) {
      console.error('Error fetching automations:', error);
      setAutomations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/projects/automation-templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const handleToggleAutomation = async (automationId, active) => {
    try {
      await api.put(`/projects/automations/${automationId}`, { active });
      setAutomations(automations.map(a => 
        a.id === automationId ? { ...a, active } : a
      ));
      toast.success(`Automação ${active ? 'ativada' : 'desativada'}`);
    } catch (error) {
      console.error('Error toggling automation:', error);
      toast.error('Erro ao atualizar automação');
    }
  };

  const handleDeleteAutomation = async (automationId) => {
    if (!confirm('Tem certeza que deseja excluir esta automação?')) return;

    try {
      await api.delete(`/projects/automations/${automationId}`);
      setAutomations(automations.filter(a => a.id !== automationId));
      toast.success('Automação excluída!');
    } catch (error) {
      console.error('Error deleting automation:', error);
      toast.error('Erro ao excluir automação');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando automações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Automações</h2>
          <p className="text-gray-600">Configure regras automáticas para seu quadro</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleSendTestEmail}
            disabled={sendingTest}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            {sendingTest ? '⏳ Enviando…' : '📧 Testar E-mail'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowTemplatesModal(true)}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            Templates
          </Button>
          <Button onClick={openCreateModal}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Nova Automação
          </Button>
        </div>
      </div>

      {/* Automations List */}
      {automations.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma automação configurada</h3>
              <p className="text-gray-600 mb-6">
                Automações ajudam a acelerar seu fluxo de trabalho executando ações automaticamente
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-center sm:gap-3">
                <Button variant="outline" onClick={() => setShowTemplatesModal(true)}>
                  Ver Templates
                </Button>
                <Button onClick={openCreateModal}>
                  Criar Primeira Automação
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {automations.map((automation) => (
            <Card key={automation.id} className={`${automation.active ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50/30'}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-lg">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{automation.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{automation.description}</p>
                      {/* Contador de execuções — ajuda a diagnosticar: se o número sobe a cada
                          teste, o gatilho está disparando (o problema seria a ação em si, ex:
                          uma ação "idempotente" que não muda nada visível na 2ª vez); se ficar
                          parado, o gatilho não está batendo. */}
                      <p className="mt-1 text-xs text-gray-400">
                        {automation.executions_count > 0
                          ? `Executada ${automation.executions_count}x${automation.last_executed ? ` · última vez em ${new Date(automation.last_executed).toLocaleString('pt-BR')}` : ''}`
                          : 'Ainda não foi executada'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={automation.active ? "default" : "secondary"}>
                      {automation.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                    <Switch
                      checked={automation.active}
                      onCheckedChange={(checked) => handleToggleAutomation(automation.id, checked)}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingAutomation(automation)}
                      className="text-blue-600 hover:text-blue-700"
                      title="Editar automação"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteAutomation(automation.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Excluir automação"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Templates Modal */}
      <Dialog open={showTemplatesModal} onOpenChange={setShowTemplatesModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Templates de Automação</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardHeader className="pb-3">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">{template.icon}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                      <Badge variant="outline" className="mt-2">
                        {template.category}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Automation Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Automação</DialogTitle>
          </DialogHeader>
          <AutomationForm key={createFormKey} board={board} api={api} onSave={() => { setShowCreateModal(false); fetchAutomations(); }} onCancel={() => setShowCreateModal(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit Automation Modal */}
      <Dialog open={Boolean(editingAutomation)} onOpenChange={open => { if (!open) setEditingAutomation(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Automação</DialogTitle>
          </DialogHeader>
          {editingAutomation && (
            <AutomationForm
              key={editingAutomation.id}
              board={board}
              api={api}
              initialData={editingAutomation}
              onSave={() => { setEditingAutomation(null); fetchAutomations(); }}
              onCancel={() => setEditingAutomation(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}